import React, { createContext, ReactNode, useContext, useState, useEffect } from 'react'
import { EventClickArg, EventContentArg } from '@fullcalendar/react'
import { pdf } from '@react-pdf/renderer'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import moment from 'moment'
import { useHistory } from 'react-router-dom';
import { sortBy } from 'lodash';

import { AnalystService } from '../services/methods/AnalystService'
import { OsService } from '../services/methods/OsService'
import { ScheduleService } from '../services/methods/ScheduleService'

import MyPdf from '../components/MyPdf'
import Os from '../components/Os'

import { ISchedule, IScheduleStruct, IProjects, Coordinators } from '../interfaces/ISchedule'
import { IAnalyst } from '../interfaces/IAnalyst'

import { getBase64 } from '../utils/getBase64'
import { useUser } from './user'

interface ScheduleProviderProps {
  children: ReactNode
}

interface IScheduleContextData {
  month: number;
  year: number;
  analystName: string;
  calendarHeight: number;
  isOpenLaunchOSModal: boolean;
  isOpenOutgoingOSModal: boolean;
  isVisualOutgoingOSModal: boolean;
  launchOsModalData: ISchedule;
  fullCalendarData: ISchedule[];
  schedules: ISchedule[];
  currentDate: string;
  isLoading: boolean;
  schedulesInfo: IScheduleStruct;
  coordinators: Coordinators[];
  filter: 'abertas' | 'apontadas' | 'remoto' | 'presencial' | '';
  setFilter: (filter: 'abertas' | 'apontadas' | 'remoto' | 'presencial' | '') => void;
  handleEventClick: (clickInfo: EventClickArg) => void;
  renderEventContent: (eventContent: EventContentArg) => void;
  loadSchedulesData: (month: number, year: number, analystCode?: string) => Promise<void>;
  handleCloseLaunchOSModal: () => void;
  handleCloseOutgoingOSModal: () => void;
  setMonth: (month: number) => void;
  setYear: (year: number) => void;
  // Allow functional updates for setFullCalendarData
  setFullCalendarData: React.Dispatch<React.SetStateAction<ISchedule[]>>;
  setCurrentDate: (date: string) => void;
  setIsLoading: (val: boolean) => void;
  updateSchedulesData: (analista: IScheduleStruct) => void;
  filterSchedules: (type: 'abertas' | 'apontadas' | 'remoto' | 'presencial' | '', data: ISchedule[]) => void;
  receiveIsUpdate: boolean;
  setReceiveIsUpdate: (value: boolean) => void;
  loadReportData: (month: number, year: number) => Promise<void>;
  projectsReport: IProjects[];
  schedulesReport: ISchedule[];
  // Analista
  analistas: IAnalyst[];
  fetchAnalystLoad: boolean;
  handleFetchAnalyst: () => Promise<void>;
  setAnalyst: (value: string) => void;
  analyst: string;
  handleDeleteOs: (idToDelete: string, recno: number) => Promise<void>;
}

const ScheduleContext = createContext({} as IScheduleContextData)

function ScheduleProvider({ children }: ScheduleProviderProps) {
  const analystService: AnalystService = new AnalystService();
  const scheduleService: ScheduleService = new ScheduleService();
  const osService: OsService = new OsService();

  const [month, setMonth] = useState(Number(moment().format('DD')) > 25 ? (Number(moment().format('MM')) + 1) > 12 ? 1 : (Number(moment().format('MM')) + 1) : Number(moment().format('MM')))
  const [year, setYear] = useState(Number(moment().format('DD')) > 25 && (Number(moment().format('MM')) + 1) > 12 ? (Number(moment().format('YYYY')) + 1) : Number(moment().format('YYYY')))
  const [analystName, setAnalystName] = useState('');
  const [schedulesInfo, setSchedulesInfo] = useState<IScheduleStruct>({} as IScheduleStruct)
  const [calendarHeight, setCalendarHeight] = useState(0)
  const [isOpenLaunchOSModal, setIsOpenLaunchOSModal] = useState(false)
  const [isOpenOutgoingOSModal, setIsOpenOutgoingOSModal] = useState(false)
  const [isVisualOutgoingOSModal, setIsVisualOutgoingOSModal] = useState(false)
  const [launchOsModalData, setLaunchOsModalData] = useState<ISchedule>({} as ISchedule)
  const [fullCalendarData, setFullCalendarData] = useState<ISchedule[]>([])
  const [schedules, setSchedules] = useState<ISchedule[]>([])
  const [currentDate, setCurrentDate] = useState(Number(moment().format('DD')) > 25 ? `${moment().format('YYYY')}-${moment().format('MM')}-26` : `${moment().format('YYYY')}-${moment().subtract(1, 'month').format('MM')}-26`)
  const [isLoading, setIsLoading] = useState(true)
  const [filter, setFilter] = useState<'abertas' | 'apontadas' | 'remoto' | 'presencial' | ''>('')
  const [receiveIsUpdate, setReceiveIsUpdate] = useState(false)
  const [projectsReport, setProjectsReport] = useState<IProjects[]>([])
  const [schedulesReport, setSchedulesReport] = useState<ISchedule[]>([])
  const [coordinators, setCoordinators] = useState<Coordinators[]>([]);
  // Analista
  const [fetchAnalystLoad, setFetchAnalystLoad] = useState(false)
  const [analistas, setAnalistas] = useState<IAnalyst[]>([])
  const [analyst, setAnalyst] = useState('')
  // Loading state for specific event card
  const [loadingEventKey, setLoadingEventKey] = useState<string | null>(null);

  const history = useHistory()
  const { userInfo } = useUser()

  async function handleFetchAnalyst() {
    setFetchAnalystLoad(true)
    const token = await sessionStorage.getItem('token')

    try {
      const { data } = await analystService.index(token!)
      if (data.status === 'ok') {
        setAnalistas(sortBy(data.dados, ['nome']))
      } else {
        toast.error(data.msg)
      }
    } catch (error) {
      toast.error('Ops algo deu errado')
      console.log(error)
    } finally {
      setFetchAnalystLoad(false)
    }
  }

  async function handleDeleteOs(idToDelete: string, recno: number) {
    try {
      // Do not set global isLoading to avoid UI flash, or handle gracefully
      const token = await sessionStorage.getItem('token');
      console.log('CONTEXT DELETE START:', idToDelete);

      const { data: deleteResponse } = await osService.deleteOS(idToDelete, token!);

      if (deleteResponse.status === 'ok') {
        toast.success(deleteResponse.msg);
        console.log('CONTEXT DELETE SUCCESS, REFRESHING RECNO:', recno);

        const analistaCode = (userInfo.cargo === 'C' && analyst !== "") ? analyst : userInfo.codigo;
        // Pass explicit recno to get "cleaned" schedule item
        const { data: responseData } = await scheduleService.index(token!, 0, 0, false, analistaCode, recno);

        if (responseData.status === 'ok' && responseData.analista.agendas.length > 0) {
          updateSchedulesData(responseData.analista);
        }
      } else {
        toast.error(deleteResponse.msg);
      }
    } catch (error: any) {
      console.error('CONTEXT DELETE ERROR:', error);
      toast.error('Erro ao excluir OS');
    }
  }

  const handleEventClick = async (clickInfo: EventClickArg) => {
    const agenda = fullCalendarData.filter(item => item.key === clickInfo.event.extendedProps.key)[0]
    const analista = (userInfo.cargo === 'C' && analyst !== "") ? analyst : userInfo.codigo;



    // Check for blocked client (legacy check preserved)
    if (clickInfo.event.extendedProps.codcli.includes('99999')) {
      return toast.error('Agenda Bloqueada.')
    }

    const token = await sessionStorage.getItem('token')

    async function deleteOs() {
      try {
        // Force update of the specific event to show loading
        setFullCalendarData(prev => prev.map(item =>
          item.key === agenda.key ? { ...item, isLoading: true } : item
        ));

        const { data, status } = await osService.deleteOS(agenda.os!, token!)
        const { msg } = data

        if (status === 200 || data.status === 'ok') {
          toast.success(msg)
          const analista = (userInfo.cargo === 'C' && analyst !== "") ? analyst : userInfo.codigo

          // REFACTOR: Use ScheduleService to refresh the slot seamlessly
          const userToken = await sessionStorage.getItem('token') || await localStorage.getItem('ethosxAnalista:token');
          // Fetch only the specific slot to update state without reloading
          const { data: scheduleResponse } = await scheduleService.index(userToken!, 0, 0, false, analista, Number(agenda.key));

          if (scheduleResponse.status === 'ok') {
            await updateSchedulesData(scheduleResponse.analista)
          } else {
            console.warn('Partial refresh warning:', scheduleResponse);
          }
        } else {
          toast.error(msg || data.message)
          // Revert loading on error
          setFullCalendarData(prev => prev.map(item =>
            item.key === agenda.key ? { ...item, isLoading: false } : item
          ));
        }
      } catch (error: any) {
        if (error.response) {
          toast.error(error.response.data.errorMessage)
        }
        console.log(error)
        // Revert loading on error
        setFullCalendarData(prev => prev.map(item =>
          item.key === agenda.key ? { ...item, isLoading: false } : item
        ));
      } finally {
        // reset states quietly
        setReceiveIsUpdate(false)
        setLoadingEventKey(null); // Cleanup old state if present
      }
    }

    const statusText = agenda.status === '1' ? 'Aberto' : agenda.status === '2' ? 'Apontado' : 'Fechado';
    const statusColor = agenda.status === '1' ? '#f0ad4e' : agenda.status === '2' ? '#5cb85c' : '#d9534f';

    const isStatus1 = agenda.status === '1';
    const isStatus2 = agenda.status === '2';
    const isRemote = agenda.local === 'REMOTO';

    Swal.fire({
      title: `<span style="font-size: 1.2rem; color: #333;">Detalhes da Agenda</span>`,
      html: `
        <div style="text-align: left; font-size: 1rem; color: #555; margin-bottom: 1.5rem;">
          <div style="margin-bottom: 8px; display: flex; align-items: center;">
            <strong style="width: 80px; color: #2778c4;">STATUS:</strong>
            <span style="background-color: ${statusColor}; color: white; padding: 2px 8px; border-radius: 4px; font-size: 0.8rem;">${statusText}</span>
          </div>
          <div style="margin-bottom: 8px;"><strong style="color: #2778c4;">DATA:</strong> ${moment(agenda.data).format('DD/MM/YYYY')}</div>
          <div style="margin-bottom: 8px;"><strong style="color: #2778c4;">CLIENTE:</strong> ${agenda.nomecli}</div>
          <div style="margin-bottom: 8px;"><strong style="color: #2778c4;">PROJETO:</strong> ${agenda.projeto}</div>
          <div style="margin-bottom: 8px;"><strong style="color: #2778c4;">LOCAL:</strong> ${agenda.local}</div>
          <div style="margin-bottom: 8px;"><strong style="color: #2778c4;">OS:</strong> ${agenda.os || '-'}</div>
        </div>

        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 1rem;">
          <button id="btn-lancar" class="swal2-confirm swal2-styled" style="width: 100%; margin: 0; background-color: ${isStatus1 ? '#2778c4' : '#e0e0e0'}; color: ${isStatus1 ? '#fff' : '#a0a0a0'}; border: none; cursor: ${isStatus1 ? 'pointer' : 'not-allowed'}; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 10px; font-size: 0.9rem;" ${!isStatus1 ? 'disabled' : ''}>
            Lançar OS
          </button>
          
          <button id="btn-alterar" class="swal2-confirm swal2-styled" style="width: 100%; margin: 0; background-color: ${isStatus2 ? '#f0ad4e' : '#e0e0e0'}; color: ${isStatus2 ? '#fff' : '#a0a0a0'}; border: none; cursor: ${isStatus2 ? 'pointer' : 'not-allowed'}; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 10px; font-size: 0.9rem;" ${!isStatus2 ? 'disabled' : ''}>
            Alterar OS
          </button>

          <button id="btn-excluir" class="swal2-confirm swal2-styled" style="width: 100%; margin: 0; background-color: ${isStatus2 ? '#d9534f' : '#e0e0e0'}; color: ${isStatus2 ? '#fff' : '#a0a0a0'}; border: none; cursor: ${isStatus2 ? 'pointer' : 'not-allowed'}; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 10px; font-size: 0.9rem;" ${!isStatus2 ? 'disabled' : ''}>
            Excluir OS
          </button>

          <button id="btn-visualizar" class="swal2-confirm swal2-styled" style="width: 100%; margin: 0; background-color: ${isStatus2 ? '#5bc0de' : '#e0e0e0'}; color: ${isStatus2 ? '#fff' : '#a0a0a0'}; border: none; cursor: ${isStatus2 ? 'pointer' : 'not-allowed'}; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 10px; font-size: 0.9rem;" ${!isStatus2 ? 'disabled' : ''}>
            Visualizar PDF
          </button>

          <button id="btn-email" class="swal2-confirm swal2-styled" style="width: 100%; margin: 0; background-color: ${isStatus2 ? '#0275d8' : '#e0e0e0'}; color: ${isStatus2 ? '#fff' : '#a0a0a0'}; border: none; cursor: ${isStatus2 ? 'pointer' : 'not-allowed'}; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 10px; font-size: 0.9rem;" ${!isStatus2 ? 'disabled' : ''}>
             Enviar Email
          </button>

          <button id="btn-despesas" class="swal2-confirm swal2-styled" style="width: 100%; margin: 0; background-color: ${isStatus2 && !isRemote ? '#DAA520' : '#e0e0e0'}; color: ${isStatus2 && !isRemote ? '#fff' : '#a0a0a0'}; border: none; cursor: ${isStatus2 && !isRemote ? 'pointer' : 'not-allowed'}; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 10px; font-size: 0.9rem;" ${!isStatus2 || isRemote ? 'disabled' : ''}>
            Despesas
          </button>
        </div>
      `,
      showConfirmButton: false,
      showCloseButton: true,
      didOpen: () => {
        // Lançar
        const btnLancar = document.getElementById('btn-lancar');
        if (btnLancar && isStatus1) {
          btnLancar.addEventListener('click', () => {
            Swal.close();
            setLaunchOsModalData(agenda);
            setIsOpenLaunchOSModal(true);
          });
        }

        // Alterar
        const btnAlterar = document.getElementById('btn-alterar');
        if (btnAlterar && isStatus2) {
          btnAlterar.addEventListener('click', () => {
            Swal.close();
            setLaunchOsModalData(agenda);
            setIsOpenLaunchOSModal(true);
          });
        }

        // Excluir
        const btnExcluir = document.getElementById('btn-excluir');
        if (btnExcluir && isStatus2) {
          btnExcluir.addEventListener('click', () => {
            // DIRECT DELETE (No Confirmation as requested)
            console.log('HOOK: Direct Delete Triggered via Calendar Button');
            Swal.close(); // Close the buttons modal immediately
            deleteOs();
          });
        }

        // Visualizar PDF
        const btnVisualizar = document.getElementById('btn-visualizar');
        if (btnVisualizar && isStatus2) {
          btnVisualizar.addEventListener('click', () => {
            // Open in new tab logic
            window.open(`/PDF/${agenda.key}/${analista}/${token}`, '_blank');
          });
        }

        // Enviar Email
        const btnEmail = document.getElementById('btn-email');
        if (btnEmail && isStatus2) {
          btnEmail.addEventListener('click', () => {
            Swal.close(); // Close main modal first
            Swal.fire({
              title: 'Email para enviar o PDF',
              input: 'email',
              inputValue: userInfo.emailEthosx,
              inputAttributes: {
                autocapitalize: 'off'
              },
              showCancelButton: true,
              confirmButtonText: 'Enviar',
              showLoaderOnConfirm: true,
              allowOutsideClick: () => !Swal.isLoading()
            }).then(async (result) => {
              if (result.isConfirmed) {
                const { data } = await osService.getOs(Number(agenda.key), analista, token!)
                if (data.status === 'ok') {
                  const blob = await pdf(<MyPdf data={data.analista.agendas[0]} name={data.analista.nome} signature={data.analista.assinatura} />).toBlob();
                  const pdf64 = await getBase64(blob)
                  try {
                    const objectParams = {
                      email: result.value,
                      cc: "",
                      cco: "",
                      subject: `Os analista - ${analystName}`,
                      body: `Os analista - ${analystName}`,
                      attach: [{ data: pdf64, name: `${analystName}OS.PDF` }],
                      token: await sessionStorage.getItem('token'),
                    };
                    const { data } = await osService.sendPdfOS(objectParams)
                    const { status, msg } = data
                    if (status === 'ok') {
                      toast.success(msg)
                    } else {
                      toast.error(msg)
                    }
                  } catch (error: any) {
                    if (error.response) {
                      toast.error(error.response.data.errorMessage)
                    }
                    console.log(error)
                  }
                }
              }
            })
          })
        }

        // Despesas
        const btnDespesas = document.getElementById('btn-despesas');
        if (btnDespesas && isStatus2) {
          btnDespesas.addEventListener('click', () => {
            if (agenda.local === 'REMOTO') {
              toast.error('Agenda remoto não possui despesa.')
              return
            }
            if (agenda.os === '') {
              toast.error('OS não lançada.')
              return
            }
            Swal.close();
            setLaunchOsModalData(agenda)
            setIsOpenOutgoingOSModal(true)
            setIsVisualOutgoingOSModal(false)
          });
        }
      }
    });
  }

  function renderEventContent(eventContent: EventContentArg) {
    // Read directly from event properties (which we updated in state)
    const isEventLoading = eventContent.event.extendedProps.isLoading === true;
    return (
      <Os
        os={eventContent.event._def.extendedProps.os}
        nomeCoordenador={eventContent.event._def.extendedProps.nomeCoordenador}
        local={eventContent.event._def.extendedProps.local}
        title={eventContent.event.title}
        subTitle={eventContent.event._def.extendedProps.projeto}
        color={eventContent.event._def.extendedProps.osColor}
        hour={eventContent.event._def.extendedProps.horatot}
        status={eventContent.event._def.extendedProps.status}
        isLoading={isEventLoading}
      />
    )
  }

  async function loadSchedulesData(month: number, year: number, analystCode: string = analyst) {
    try {
      setIsLoading(true)
      const userToken = await sessionStorage.getItem('token') || await localStorage.getItem('ethosxAnalista:token')
      const { data: schedulesResponse } = await scheduleService.index(userToken!, month, year, false, analystCode)
      const { analista, msg, status } = schedulesResponse
      setSchedulesInfo(analista)
      if (status === 'ok') {
        await sessionStorage.setItem('analystName', analista.nome)
        setAnalystName(analista.nome)
        const analistaOs: ISchedule[] = []
        analista.agendas.map((item: ISchedule) => {
          let addAgenda = true;
          item.title = `${item.nomecli}`;
          item.start = item.data
          item.osColor = item.color === '' ? '#888' : item.color
          item.color = 'transparent'

          analistaOs.push(item)

          return item
        })

        const key = 'nomeCoordenador';
        const arrayUniqueByKey = [...new Map(analista.agendas.map((item: ISchedule) => [item[key], { nome: item.nomeCoordenador.split(' ')[0], color: item.osColor! }])).values()];
        setCoordinators(arrayUniqueByKey as Coordinators[])

        const date = moment(analista.currentDate).subtract('months', 1).set('date', 25).format('YYYY-MM-DD')//Number(moment().format('DD')) > 25 ? moment(analista.currentDate).set('date', 26).format('YYYY-MM-DD') : moment(analista.currentDate).subtract(1, 'month').set('date', 26).format('YYYY-MM-DD')
        setCurrentDate(date)

        setFullCalendarData(analistaOs)
        setSchedules(analistaOs)

        const currentMonth = Number(moment().format('DD')) > 25 ? (Number(moment().format('MM')) + 1) > 12 ? 1 : (Number(moment().format('MM')) + 1) : Number(moment().format('MM'));
        const currentYear = Number(moment().format('DD')) > 25 && (Number(moment().format('MM')) + 1) > 12 ? (Number(moment().format('YYYY')) + 1) : Number(moment().format('YYYY'));

        setReceiveIsUpdate(true)
        if (analista.agendas.filter((item: ISchedule) => item.os === '' && moment(item.data).isBefore(moment(new Date()).format('YYYY-MM-DD'))).length > 0 && month === currentMonth && year === currentYear) {
          Swal.fire({
            title: 'Atenção',
            html: `<span style="font-size: 14px;">Você tem <strong>${analista.agendas.filter((item: ISchedule) => item.os === '' && moment(item.data).isBefore(moment(new Date()).format('YYYY-MM-DD'))).length}</strong> agenda atrasada! Por favor aponte as agendas antes do fechamento do mês.</span>`,
            icon: 'warning',
            confirmButtonText: 'Ok'
          })
        }
      } else {
        toast.error(msg)
      }

    } catch (error: any) {
      if (error.response) {
        toast.error(error.response.data.errorMessage)
      }
      await localStorage.removeItem('ethosxAnalista:token')
      await sessionStorage.removeItem('analystName')
      await sessionStorage.removeItem('token')

      toast.error('Algo deu errado faça login novamente.')
      history.push('/login')
    } finally {
      setIsLoading(false)
    }
  }

  async function loadReportData(month: number, year: number) {
    try {
      setIsLoading(true)
      setProjectsReport([])
      const userToken = await sessionStorage.getItem('token') || await localStorage.getItem('ethosxAnalista:token')
      const { data: schedulesResponse } = await scheduleService.index(userToken!, month, year, true)
      const { analista, msg, status } = schedulesResponse

      if (status === 'ok') {

        const analistaOs: ISchedule[] = []
        const projectOS: IProjects[] = []
        analista.agendas.map((item: ISchedule) => {
          if (item.horatot === 0) {
            item.title = analista.cargo === 'C' ? `${item.analista} - ${item.nomecli} - ${item.projeto}` : `${item.nomecli} - ${item.projeto}`;
          } else {
            item.title = `${item.horatot} - ${item.nomecli}`;
          }
          item.start = item.data

          analistaOs.push(item)
          if (item.os !== '') {
            const projectExist = projectOS.find(project => project.nome === item.projeto)
            if (projectExist === undefined) {
              projectOS.push({
                nome: item.projeto,
                valor: item.valortot,
                horas: item.horatot,
                despesa: item.totDesp
              })
            } else {
              const index = projectOS.findIndex(project => project.nome === item.projeto)

              projectOS[index].valor = projectOS[index].valor + item.valortot
              projectOS[index].horas = projectOS[index].horas + item.horatot
              projectOS[index].despesa = projectOS[index].despesa + item.totDesp
            }
          }
          // analistaOs.push(item)

          return item
        })
        setProjectsReport(projectOS)
        setSchedulesReport(analistaOs)
      } else {
        toast.error(msg)
      }

    } catch (error: any) {
      console.log(error)
      if (error.response) {
        toast.error(error.response.data.errorMessage)
      }
      await localStorage.removeItem('ethosxAnalista:token')
      await sessionStorage.removeItem('analystName')
      await sessionStorage.removeItem('token')

      toast.error('Algo deu errado faça login novamente.')
      history.push('/login')
    } finally {
      setIsLoading(false)
    }
  }

  async function updateSchedulesData(analista: IScheduleStruct) {
    const newFullCalendarData: ISchedule[] = []
    const newSchedules: ISchedule[] = []
    analista.agendas.map((item: ISchedule) => {
      item.title = `${item.nomecli}`;
      item.start = item.data
      item.osColor = item.color === '' ? '#888' : item.color
      item.color = 'transparent'
      item.start = item.data
      fullCalendarData.map(data => {
        if (data.key === item.key) {
          newFullCalendarData.push(item)
        } else {
          newFullCalendarData.push(data)
        }
        return data
      })

      schedules.map(schedule => {
        if (schedule.key === item.key) {
          newSchedules.push(item)
        } else {
          newSchedules.push(schedule)
        }
        return schedule
      })
      return item
    })
    // setCurrentDate(analista.currentDate)
    setFullCalendarData(newFullCalendarData)
    setSchedules(newSchedules)
    filterSchedules(filter, newSchedules)
  }

  async function filterSchedules(type: 'abertas' | 'apontadas' | 'remoto' | 'presencial' | '', data: ISchedule[]) {
    // setIsLoading(true) -> Removed to prevent calendar blink
    setFilter(type)
    if (type === 'abertas') {
      const filterSchedulesAbertas = data.filter(item => item.os === '')
      setFullCalendarData(filterSchedulesAbertas)
    }
    if (type === 'apontadas') {
      const filterSchedulesApontadas = data.filter(item => item.os !== '')
      setFullCalendarData(filterSchedulesApontadas)
    }
    if (type === 'remoto') {
      const filterSchedulesRemota = data.filter(item => item.local === 'REMOTO')
      setFullCalendarData(filterSchedulesRemota)
    }
    if (type === 'presencial') {
      const filterSchedulesPresencial = data.filter(item => item.local !== 'REMOTO')
      setFullCalendarData(filterSchedulesPresencial)
    }
    if (type === '') {
      setFullCalendarData(data)
    }
    if (filter === type) {
      setFilter('')
      setFullCalendarData(data)
    }
    // setIsLoading(false)
  }

  // LauchOSModal
  const handleCloseLaunchOSModal = () => {
    setIsOpenLaunchOSModal(false)
  }

  const handleDeleteOS = async (idToDelete: string, recno: number) => {
    try {
      console.log('PARENT (SCHEDULE) DELETING OS:', idToDelete);
      const userToken = await sessionStorage.getItem('token') || await localStorage.getItem('ethosxAnalista:token');

      // 1. Delete
      const { data: deleteResponse } = await osService.deleteOS(idToDelete, userToken!)

      if (deleteResponse.status === 'ok') {
        toast.success(deleteResponse.msg)

        // 2. Refresh
        const analistaCode = (userInfo.cargo === 'C' && analyst !== "") ? analyst : userInfo.codigo
        const { data: responseData } = await scheduleService.index(userToken!, 0, 0, false, analistaCode, recno)

        if (responseData.status === 'ok' && responseData.analista.agendas.length > 0) {
          updateSchedulesData(responseData.analista)
        }
      } else {
        toast.error(deleteResponse.msg)
      }

    } catch (error) {
      console.error('DELETE ERROR:', error);
      toast.error('Erro ao excluir OS');
    }
  }

  const handleCloseOutgoingOSModal = () => {
    setIsOpenOutgoingOSModal(false)
    setIsVisualOutgoingOSModal(false)
  }

  useEffect(() => {
    setCalendarHeight(window.innerHeight)
  }, [])

  return (
    <ScheduleContext.Provider value={{
      month,
      year,
      analystName,
      calendarHeight,
      isOpenLaunchOSModal,
      isOpenOutgoingOSModal,
      isVisualOutgoingOSModal,
      launchOsModalData,
      fullCalendarData,
      schedules,
      coordinators,
      filter,
      setFilter,
      currentDate,
      isLoading,
      handleEventClick,
      renderEventContent,
      loadSchedulesData,
      schedulesInfo,
      handleCloseLaunchOSModal,
      handleCloseOutgoingOSModal,
      setMonth,
      setYear,
      setFullCalendarData,
      setCurrentDate,
      setIsLoading,
      updateSchedulesData,
      filterSchedules,
      receiveIsUpdate,
      setReceiveIsUpdate,
      loadReportData,
      projectsReport,
      schedulesReport,
      analistas,
      fetchAnalystLoad,
      handleFetchAnalyst,
      setAnalyst,
      analyst,
      handleDeleteOs
    }}>
      {children}
    </ScheduleContext.Provider>
  )
}

function useSchedule() {
  const context = useContext(ScheduleContext)

  return context
}

export { ScheduleProvider, useSchedule }